<template>
    <div v-if="!asset.loaded" class="placeholder-container">
        <span class="placeholder-preview"></span>
        <span class="placeholder-label"></span>
    </div>

    <div v-else class="compare-view-item-container" :data-index="asset.id">
        <img :src="thumbnail" @load="revokeObjectURL(thumbnail)" @dblclick="openAsset($event)" :data-index="asset.id" />
        <div class="label-container">
            <span class="name">{{
					asset.type == 'media' ? stripTimestamp(`${asset.name}.${asset.fileType}`) : stripTimestampAndExtension(asset.name)
                }}</span>
        </div>

        <fileTags :file="asset" />

        <v-menu offset-y :content-class="'file-options-container'">
            <template v-slot:activator="{ on }">
                <i class="material-icons" style="cursor: pointer" v-on="on">more_vert</i>
            </template>
            <v-list>
                <v-list-item
                    @click="downloadLUT(asset)"
                    v-if="!isGuest"
                    :disabled="!canExportLUT"
                >
                    <v-icon class="material-symbols-outlined">deployed_code_update</v-icon>
                    <v-list-item-title>Download a LUT</v-list-item-title>
                </v-list-item>

                <v-list-item
                    @click="downloadXMP(asset)"
                    v-if="!isGuest"
                    :disabled="!canExportXMP"
                >
                    <img src="/assets/icons/lr-icon-96x96.png" />
                    <v-list-item-title>Download XMP Profile</v-list-item-title>
                </v-list-item>

                <v-list-item
                    @click="downloadCO(asset)"
                    v-if="!isGuest"
                    :disabled="!canExportCO"
                >
                    <img src="/assets/icons/co-icon-96x96.png" />
                    <v-list-item-title>Download Capture One Style</v-list-item-title>
                </v-list-item>

                <v-menu
                    offset-x
                    :nudge-top="20"
                    open-on-hover
                    :content-class="'file-options-container --rating --level2'"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on" :disabled="!canAssignTagToAsset">
                            <i class="material-icons">star_rate</i>
                            <v-list-item-title>Rating</v-list-item-title>
                            <i class="material-icons">arrow_right</i>
                        </v-list-item>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="ratingTag in tags.rating"
                            :key="ratingTag.value"
                            class="stars-icons-container"
                            :class="{ '--selected': fileHasTag(asset, 'rating', ratingTag.value) }"
                            @click="addTag(asset, 'rating', ratingTag.value)"
                        >
                            <v-list-item-title v-if="ratingTag.value == 'none'">{{
									ratingTag.label
                                }}
                            </v-list-item-title>
                            <i v-else v-for="index in ratingTag.value" :key="index" class="material-icons">star_rate</i>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-menu
                    offset-x
                    :nudge-top="20"
                    open-on-hover
                    :content-class="'file-options-container --level2'"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on" :disabled="!canAssignTagToAsset">
                            <i class="material-icons">fiber_manual_record</i>
                            <v-list-item-title>Status</v-list-item-title>
                            <i class="material-icons">arrow_right</i>
                        </v-list-item>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="statusTag in tags.status"
                            :key="statusTag.value"
                            :class="{ '--selected': fileHasTag(asset, 'status', statusTag.value) }"
                            @click="addTag(asset, 'status', statusTag.value)"
                        >
                            <i class="material-icons" :style="{ color: statusTag.styleColor }">fiber_manual_record</i>
                            <v-list-item-title>{{ statusTag.label }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-menu
                    offset-x
                    :nudge-top="20"
                    open-on-hover
                    :content-class="'file-options-container --level2'"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-list-item v-bind="attrs" v-on="on" :disabled="!canAssignTagToAsset">
                            <i class="material-icons">label</i>
                            <v-list-item-title>Tag</v-list-item-title>
                            <i class="material-icons">arrow_right</i>
                        </v-list-item>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="colorTag in tags.color"
                            :key="colorTag.value"
                            :class="{ '--selected': fileHasTag(asset, 'color', colorTag.value) }"
                            @click="addTag(asset, 'color', colorTag.value)"
                        >
                            <i class="material-icons" :style="{ color: colorTag.styleColor }">label</i>
                            <v-list-item-title>{{ colorTag.label }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import CanMixin from '@/mixins/CanMixin';
import FilenameMixin from '@/mixins/Filename';
import { mapActions, mapGetters, mapState } from 'vuex';
import fileTags from '@/components/app/files/FileTags.vue';
import { stripExtension } from "@/assets/js/Utilities";

export default {
    components: {fileTags},
    mixins: [CanMixin, FilenameMixin],

    data() {
        return {
            thumbnail: null
        };
    },

    computed: {
        ...mapState('Projects', ['tags']),
        ...mapGetters('User', ['isGuest']),
    },

    props: {
        asset: Object,
    },

    created() {
        if (this.asset.loaded) this.thumbnail = URL.createObjectURL(this.asset.image);
    },

    methods: {
        ...mapActions('Helpers', ['revokeObjectURL']),

        fileHasTag(file, tagName, value) {
            if (file.tags) return file.tags.find(tag => tag.name == tagName && tag.value == value);
            else return false;
        },

        async addTag(file, tag_name, tag_value) {

            await this.$root.projectTree.addTag(file, tag_name, tag_value);

            let freshAsset = this.$store.getters['Projects/assetById'](this.asset.id, this.asset.type);
            this.$store.commit('UI/UPDATE_COMPARE_VIEW_ITEM', {
                key: 'tags',
                assetId: this.asset.id,
                value: freshAsset.tags
            })
        },

        async openAsset($event) {
            UI.hideModal(UI.modals.compareView.el, true);
            await this.$root.projectTree.onMediaSelection($event, this.asset.assetObject);
        },

        async downloadLUT(selectedFile) {
            if (lutify.isCurrentPlanFree()) {
                UI.showSecondLevelModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let assetsStoreData = [];
            for await (const file of [selectedFile]) {
                if (file.type == "image" || file.type == "media") {
                    const image = await window.lutify.fetchImage(file);
                    await window.lutify.storeLastState();
                    const { blocks } = await window.lutify.fetchLastState(file.id);
                    image.blocks = blocks;
                    image.nicename = stripExtension(image.name);
                    assetsStoreData.push(image);
                }
                else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
                else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            }

            await this.$store.dispatch("Projects/setAction", {
                type: this.asset.type,
                action: "export-lut-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-lut');
            window.UI.hideModal(lutify.activeModal, true);
        },

        async downloadXMP(selectedFile) {
            if (lutify.isCurrentPlanFree()) {
                UI.showSecondLevelModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let assetsStoreData = [];
            for await (const file of [selectedFile]) {
                if (file.type == "image" || file.type == "media") {
                    const image = await window.lutify.fetchImage(file);
                    await window.lutify.storeLastState();
                    const { blocks } = await window.lutify.fetchLastState(file.id);
                    image.blocks = blocks;
                    image.nicename = stripExtension(image.name);
                    assetsStoreData.push(image);
                }
                else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
                else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            }

            await this.$store.dispatch("Projects/setAction", {
                type: this.asset.type,
                action: "export-xmp-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-xmp');
            window.UI.hideModal(lutify.activeModal, true);
        },

        async downloadCO(selectedFile) {
            if (lutify.isCurrentPlanFree()) {
                UI.showSecondLevelModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let assetsStoreData = [];
            for await (const file of [selectedFile]) {
                if (file.type == "image") {
                    const image = await window.lutify.fetchImage(file);
                    await window.lutify.storeLastState();
                    const { blocks } = await window.lutify.fetchLastState(file.id);
                    image.blocks = blocks;
                    image.nicename = stripExtension(image.name);
                    assetsStoreData.push(image);
                }
                else if (file.type == "candidate") assetsStoreData.push(await window.lutify.fetchCandidate(file));
                else if (file.type == "match") assetsStoreData.push(await window.lutify.fetchMatch(file));
            }

            await this.$store.dispatch("Projects/setAction", {
                type: this.asset.type,
                action: "export-co-from-asset",
                data: assetsStoreData,
            });

            this.$store.dispatch('UI/setRightColumn', 'download-co');
            window.UI.hideModal(lutify.activeModal, true);
        }
    }
};
</script>

<style lang="scss" scoped>
.placeholder-container {
  .placeholder-preview {
    height: auto;
    aspect-ratio: 16/9;
  }

  .placeholder-label {
    height: 20px;
  }
}

.compare-view-item-container {
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-template-rows: 1fr auto;
  grid-row-gap: 5px;
  grid-column-gap: 4px;
  align-items: center;
  padding-bottom: 2px;
  border: 2px solid transparent;

  &:hover {
    border: 2px solid var(--primary);
    border-radius: 8px;
  }

  img {
    grid-column: 1 / 4;
    border-radius: 8px;

  }

  .label-container {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
    padding-left: 5px;
  }
}
</style>
